/**
 * CR-7750: what the SUPPLIER sees once they have submitted.
 *
 * The customer gets PendingReviewPanel with Accept / Reject; the supplier gets
 * this — confirmation that it is with the customer, and the two things they
 * may still do while it is open: amend it, or withdraw it.
 */
import { useState } from 'react';
import { Clock, Loader2, Pencil, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { qualityAPI } from '../../lib/api';
import type { ApprovalRegisterPending } from '../../lib/api';

export interface SupplierPendingNoticeProps {
  pending: ApprovalRegisterPending;
  formatDate: (value: string | null | undefined) => string;
  /** Passes the submission back so the caller can open the amend dialog on
   *  it. Raised here rather than closed over by the caller, which is what
   *  kept the amend path from ever receiving one. */
  onAmend: (pending: ApprovalRegisterPending) => void;
  onChanged: () => void;
}

export default function SupplierPendingNotice({
  pending,
  formatDate,
  onAmend,
  onChanged,
}: SupplierPendingNoticeProps) {
  const [busy, setBusy] = useState(false);

  const withdraw = async () => {
    try {
      setBusy(true);
      await qualityAPI.withdrawAccreditationSubmission(pending.id);
      toast.success('Submission withdrawn');
      onChanged();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not withdraw it';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-700 dark:bg-amber-900/20">
      <div className="flex items-center justify-between">
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          <Clock className="mr-1 h-3 w-3" />
          With your customer
        </Badge>
        <span className="text-xs text-muted-foreground">
          Sent {formatDate(pending.submitted_at)}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        Your customer is reviewing this. The details above stay as they are
        until they accept it.
      </p>
      <dl className="space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">You submitted expiry</dt>
          <dd>
            {pending.no_expiry_applies
              ? 'No expiry applies'
              : formatDate(pending.expiry_date)}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Certificate</dt>
          <dd>{pending.has_certificate ? 'Attached' : 'Not attached'}</dd>
        </div>
      </dl>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={withdraw}>
          {busy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            : <Undo2 className="mr-1 h-4 w-4" />}
          Withdraw
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onAmend(pending)}
        >
          <Pencil className="mr-1 h-4 w-4" />
          Amend
        </Button>
      </div>
    </div>
  );
}
